/**
 * Create-anything agent (twinnee.creation.v1):
 * intent → flow → GPT JSON plan → answer pending fields in chat → execute.
 */
import api from '@/lib/api';
import { STORY_MASTER_JOB_TYPES } from '@/lib/taskHistoryScope';

export async function fetchCreationConfig() {
  const { data } = await api.get('/creation/config');
  return { unifiedOnly: Boolean(data?.unified_only) };
}

export async function fetchStoryContext(planId) {
  const { data } = await api.get(`/creation/plans/${planId}/context`);
  return data;
}

export async function retryStoryAnalysis(planId) {
  const { data } = await api.post(`/creation/plans/${planId}/context/analyze`, { force: true });
  return data;
}

export async function patchStoryContext(planId, body) {
  const { data } = await api.post(`/creation/plans/${planId}/context/patch`, body);
  return data;
}

export async function confirmStoryContext(planId, body) {
  const { data } = await api.post(`/creation/plans/${planId}/context/confirm`, body);
  return data;
}

export async function updateStoryFormat(planId, body) {
  const { data } = await api.post(`/creation/plans/${planId}/format`, body);
  return data;
}

export async function updateStorySource(planId, body) {
  const { data } = await api.post(`/creation/plans/${planId}/context/sources`, body);
  return data;
}

export async function lockStoryCharacters(planId, body) {
  const { data } = await api.post(`/creation/plans/${planId}/context/characters`, body);
  return data;
}

export async function draftStoryScript(planId) {
  const { data } = await api.post(`/creation/plans/${planId}/context/script`);
  return data;
}

export async function requestStoryScreenplay(planId, body = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/screenplay`, body);
  return data;
}

export async function writeStructuredScreenplay(planId, body = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/screenplay/structured`, body);
  return data;
}

export async function editStructuredScreenplay(planId, body) {
  const { data } = await api.post(`/creation/plans/${planId}/screenplay/structured/edit`, body);
  return data;
}

export async function approveStructuredScreenplay(planId, screenplayId) {
  const { data } = await api.post(`/creation/plans/${planId}/screenplay/structured/approve`, { screenplay_id: screenplayId });
  return data;
}

export async function approveStoryScreenplay(planId, versionId) {
  const { data } = await api.post(`/creation/plans/${planId}/screenplay/approve`, { version_id: versionId });
  return data;
}

export async function createCreationPlan({
  text = '',
  transcript = '',
  extractedText = '',
  attachmentIds = [],
  productionProjectId = null,
  preferredFlowId = '',
  seedInput = null,
}) {
  const { data } = await api.post('/creation/plans', {
    text,
    transcript,
    extracted_text: extractedText,
    attachment_ids: attachmentIds,
    production_project_id: productionProjectId,
    preferred_flow_id: preferredFlowId,
    ...(seedInput && typeof seedInput === 'object' ? { seed_input: seedInput } : {}),
  });
  return data.plan;
}

export async function answerCreationPlan(planId, { field, value, message, acceptSuggestions, skip } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/answers`, {
    field: field || null,
    value: value ?? null,
    message: message || '',
    accept_suggestions: Boolean(acceptSuggestions),
    skip: Boolean(skip),
  });
  return data.plan;
}

/** Start composing the plan's song with AI; the plan's `audio_generation` tracks progress. */
export async function generateCreationAudio(planId, { prompt = '', lyrics = '', instrumental = false } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/generate-audio`, { prompt, lyrics, instrumental });
  return data.plan;
}

/** Write a spoken script from the plan's title/brief and apply it as the script answer. */
export async function generateCreationScript(planId, { prompt = '' } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/generate-script`, { prompt });
  return { plan: data.plan, script: data.script || '' };
}

/** Narrator voices for the plan's language: `{ language, suggested, voices }`. */
export async function fetchVoiceDesignBrief(planId, { slot = 'narrator' } = {}) {
  const { data } = await api.get(`/creation/plans/${planId}/voice-design`, { params: { slot } });
  return data || {};
}

/** ElevenLabs voice-design previews for a plan slot (`narrator` or `character:{id}`). */
export async function requestVoiceDesign(
  planId,
  {
    slot = 'narrator',
    remixVoiceId = '',
    voiceDescription = '',
    language = '',
    gender = '',
    referenceAudioBase64 = '',
  } = {},
) {
  const { data } = await api.post(
    `/creation/plans/${planId}/voice-design`,
    {
      slot,
      remix_voice_id: remixVoiceId || undefined,
      voice_description: voiceDescription || undefined,
      language: language || undefined,
      gender: gender || undefined,
      reference_audio_base64: referenceAudioBase64 || undefined,
    },
    { timeout: referenceAudioBase64 ? 180000 : undefined },
  );
  return {
    slot: data?.slot || slot,
    previews: Array.isArray(data?.previews) ? data.previews : [],
    design: data?.design || null,
  };
}

/** Create a library voice from a design preview and apply it to the plan. */
export async function commitVoiceDesign(planId, { slot, generatedVoiceId, voiceName = '' } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/voice-design/commit`, {
    slot,
    generated_voice_id: generatedVoiceId,
    voice_name: voiceName || undefined,
  });
  return { plan: data?.plan, voice: data?.voice, design: data?.design };
}

/** Design + create from the first preview (fallback to catalog default on the server). */
export async function autoVoiceDesign(planId, { slot = 'narrator' } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/voice-design/auto`, { slot });
  return { plan: data?.plan, voice: data?.voice, slot: data?.slot || slot };
}

export function castVoiceSlotId(rowKey) {
  if (!rowKey || rowKey === 'narrator') return 'narrator';
  return `character:${rowKey}`;
}

export async function fetchCreationVoices(planId, { language = '' } = {}) {
  const { data } = await api.get(`/creation/plans/${planId}/voices`, { params: language ? { language } : {} });
  return {
    language: data?.language || 'en',
    suggested: Array.isArray(data?.suggested) ? data.suggested : [],
    voices: Array.isArray(data?.voices) ? data.voices : [],
    script_language: data?.script_language || null,
  };
}

export const VOICE_PROVIDER_LABELS = { murf: 'Murf', elevenlabs: 'ElevenLabs', minimax: 'MiniMax' };

const VOICE_SAMPLE_TEXT = {
  en: "Hi! I'd love to narrate your story. Here's how I sound.",
  hi: 'नमस्ते! मैं आपकी कहानी सुनाऊँगा। मेरी आवाज़ कुछ ऐसी है।',
  es: '¡Hola! Me encantaría narrar tu historia. Así sueno yo.',
  fr: "Bonjour ! J'aimerais raconter votre histoire. Voici ma voix.",
  de: 'Hallo! Ich erzähle gern deine Geschichte. So klinge ich.',
};

/**
 * How to play a voice sample: its stored preview when that is in the target language, otherwise a
 * short line synthesized by the provider. Murf previews are recorded in the voice's own locale.
 */
export function voiceSampleSource(voice, language = 'en') {
  if (voice?.preview_url && (voice.native || voice.provider !== 'murf')) return { url: voice.preview_url };
  return {
    body: {
      provider: voice?.provider,
      voice_id: voice?.voice_id,
      locale: voice?.locale || undefined,
      text: VOICE_SAMPLE_TEXT[language] || VOICE_SAMPLE_TEXT.en,
    },
  };
}

export function voiceMeta(voice) {
  const gender = voice?.gender ? voice.gender[0].toUpperCase() + voice.gender.slice(1) : '';
  return [gender, voice?.accent && voice.accent.replace(/_/g, ' '), voice?.locale].filter(Boolean).join(' · ');
}

/** Search by name, id, accent or description; `gender` is 'all' | 'female' | 'male'. */
export function filterVoices(voices, { query = '', gender = 'all' } = {}) {
  const q = query.trim().toLowerCase();
  return (voices || []).filter((v) => {
    if (gender !== 'all' && v.gender !== gender) return false;
    if (!q) return true;
    return [v.name, v.voice_id, v.accent, v.locale, v.description].some((s) => String(s || '').toLowerCase().includes(q));
  });
}

export function voiceAnswer(voice) {
  return {
    value: { provider: voice.provider, voice_id: voice.voice_id, locale: voice.locale || '', name: voice.name || '' },
    label: `Use ${voice.name || voice.voice_id}`,
  };
}

export async function executeCreationPlan(planId, { mode = 'auto', force = false } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/execute`, { mode, force });
  return data;
}

/** Retry the plan's failed task from the step that failed; returns `{ plan, execution }` tracking the new job. */
export async function retryCreationPlan(planId, { restart = false, continueFrom = false } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/retry`, {
    restart,
    continue_from: continueFrom,
  });
  return data;
}

/**
 * Render the finished plan's final video again from the project's current scenes (same job).
 * `soundDesign` first plans and generates scene music, ambience and sound effects.
 */
/** Compile / retry the final MP4 from current scenes (no Open project required). */
export async function exportFinalCreationPlan(planId) {
  const { data } = await api.post(`/creation/plans/${planId}/export-final`);
  return data;
}

const SFX_IN_PROGRESS = new Set([
  'planned', 'resolving', 'generating_asset', 'mixing', 'validating',
  'waiting_for_alignment', 'waiting_for_video',
]);

/** One line for the create chat and the Output panel. Empty when this story has no SFX step. */
export function sfxStepLabel(sound) {
  if (!sound || (!sound.include_sfx && !sound.sfx_ready)) return '';
  if (sound.sfx_ready) return 'Sound effects generated';
  const status = String(sound.sfx_status || '');
  if (status === 'failed') return 'Sound effects failed';
  if (SFX_IN_PROGRESS.has(status)) return 'Generating sound effects';
  return 'Sound effects not generated yet';
}

export async function rerenderCreationPlan(planId, { soundDesign = false } = {}) {
  const { data } = await api.post(`/creation/plans/${planId}/rerender`, { sound_design: soundDesign });
  return data;
}

/** Convert still scene images to AI videos (multi-speaker focus), then re-render the final MP4. */
export async function convertCreationPlanImagesToVideos(planId) {
  const { data } = await api.post(`/creation/plans/${planId}/convert-images-to-videos`);
  return data;
}

/** Flows whose failed tasks can be retried from the failed step (see backend RESUMABLE_FLOWS). */
export const RETRYABLE_FLOWS = ['narration', 'script_based', 'news_video', 'dialogue'];

export function canRetryExecution(execution, job) {
  return Boolean(
    execution?.mode === 'api' &&
      execution.job_id &&
      RETRYABLE_FLOWS.includes(execution.flow_id) &&
      (job?.status === 'failed' || job?.status === 'cancelled')
  );
}

/** Dialogue stories can be restarted after execute (finished or stuck) so script/portraits are used. */
export function canRestartDialogueStory(execution) {
  if (execution?.mode !== 'api' || execution.flow_id !== 'dialogue') return false;
  return Boolean(execution.project_id || execution.job_id);
}

/* ---- Dialogue-story cast: overview → characters (looks, traits) → voices ---- */

const CAST_LONG_TIMEOUT_MS = 450000;

export const CAST_STAGES = ['overview', 'characters', 'voices', 'done'];

export const CAST_STAGE_LABELS = {
  overview: 'Story overview',
  characters: 'Build characters',
  voices: 'Allocate voices',
  done: 'Cast ready',
};

export const CHARACTER_ROLES = ['hero', 'friend', 'villain', 'animal', 'imaginary', 'narrator'];

export const CHARACTER_GENDERS = [
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'non_binary', label: 'Non-binary' },
  { id: 'not_specified', label: 'Not specified' },
];

/** Next cast step for a dialogue plan ('' when the flow has no cast step). */
export function castStage(plan) {
  return plan?.cast_status?.stage || '';
}

export function usesCast(plan) {
  return Boolean(plan?.cast_status);
}

async function castPost(planId, path, body, config) {
  const { data } = await api.post(`/creation/plans/${planId}/cast/${path}`, body ?? {}, config);
  return data.plan;
}

export function writeCastOverview(planId, { text, regenerate = false } = {}) {
  return castPost(planId, 'overview', text != null ? { text } : { regenerate }, { timeout: CAST_LONG_TIMEOUT_MS });
}

export function detectCastCharacters(planId) {
  return castPost(planId, 'detect', {}, { timeout: CAST_LONG_TIMEOUT_MS });
}

export function addCastCharacter(planId, fields) {
  return castPost(planId, 'characters', fields);
}

export function fillCastProfiles(planId) {
  return castPost(planId, 'profiles', {});
}

export async function updateCastCharacter(planId, charId, fields) {
  const { data } = await api.put(`/creation/plans/${planId}/cast/characters/${charId}`, fields);
  return data.plan;
}

export async function removeCastCharacter(planId, charId) {
  const { data } = await api.delete(`/creation/plans/${planId}/cast/characters/${charId}`);
  return data.plan;
}

export function generateCastPortrait(planId, charId) {
  return castPost(planId, `characters/${charId}/portrait`, {}, { timeout: CAST_LONG_TIMEOUT_MS });
}

/** Upload a portrait photo for a cast character, then make it the character's look in this plan. */
export async function uploadCastPhoto(planId, charId, file) {
  const form = new FormData();
  form.append('file', file);
  await api.post(`/characters/${charId}/upload-photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return castPost(planId, 'refresh', { uploaded_character_id: charId });
}

export function refreshCast(planId) {
  return castPost(planId, 'refresh', {});
}

export function confirmCastCharacters(planId) {
  return castPost(planId, 'confirm', { step: 'characters' });
}

/** `assignments` maps character id → voice pick; `auto` fills every character (and narrator) still without one. */
export function assignCastVoices(planId, { assignments = {}, narrator = null, auto = false, confirm = true } = {}) {
  return castPost(planId, 'voices', { assignments, narrator, auto, confirm });
}

export function voicePickFromVoice(voice) {
  return voiceAnswer(voice).value;
}

function _voiceLooksChild(voice) {
  const blob = `${voice?.age || ''} ${voice?.name || ''} ${voice?.description || ''} ${voice?.voice_id || ''}`.toLowerCase();
  if (/\b(child|kid|boy|girl|little|toddler|preteen|pre-teen|young boy|young girl)\b/.test(blob)) return 2;
  if (/\b(young|youth|teen|teenage)\b/.test(blob) && !/\b(adult|man|woman|mature|middle[- ]?aged|elderly)\b/.test(blob)) return 1;
  if (/\b(adult|man|woman|mature|middle[- ]?aged|elderly|old)\b/.test(blob)) return -1;
  return 0;
}

export function isChildCharacter(member) {
  if (String(member?.voice_style || '').toLowerCase() === 'child') return true;
  const age = Number(member?.age);
  if (Number.isFinite(age) && age > 0 && age <= 12) return true;
  const blob = `${member?.look || ''} ${member?.appearance || ''} ${member?.description || ''}`;
  return /\b(child|kid|toddler|little boy|little girl|young boy|young girl|\d{1,2}\s*-?\s*year)/i.test(blob);
}

/** Voices ranked for one character: kids first when the character is a child, then gender, then catalog order. */
export function voicesForMember(voices, member) {
  const gender = ['female', 'male'].includes(member?.gender) ? member.gender : '';
  const child = isChildCharacter(member);
  if (!gender && !child) return [...(voices || [])];
  return [...(voices || [])].sort((a, b) => {
    if (child) {
      const ageRank = _voiceLooksChild(b) - _voiceLooksChild(a);
      if (ageRank) return ageRank;
    }
    if (gender) {
      const genderRank = Number(b.gender === gender) - Number(a.gender === gender);
      if (genderRank) return genderRank;
    }
    return 0;
  });
}

export function castMemberVoiced(member) {
  return Boolean(member?.voice?.voice_id);
}

export async function fetchCreationOutputs(planId) {
  const { data } = await api.get(`/creation/plans/${planId}/outputs`);
  return data;
}

export function hasCreationOutputs(execution) {
  return Boolean(execution?.mode === 'api' && (execution.project_id || execution.job_id));
}

export const EXPORT_PLATFORM_LABELS = {
  youtube: 'YouTube',
  instagram: 'Instagram',
};

/** Export history rows: `platform` null means a rendered MP4 file (download), otherwise a social post. */
export function exportPlatformLabel(platform) {
  return EXPORT_PLATFORM_LABELS[platform] || 'File';
}

export const CREATE_AGENT_PARAM = 'agent';
export const CREATE_STORY_PARAM = 'story';
export const CREATE_FLOW_PARAM = 'flow';

/**
 * Query string with the create-agent plan (`agent`), started story/project (`story`),
 * and optional preferred flow (`flow`). A string sets the param, `null` removes it,
 * `undefined` leaves it unchanged.
 */
export function withCreateAgentParams(search, { planId, storyId, flowId } = {}) {
  const params = new URLSearchParams(search || '');
  [
    [CREATE_AGENT_PARAM, planId],
    [CREATE_STORY_PARAM, storyId],
    [CREATE_FLOW_PARAM, flowId],
  ].forEach(([key, value]) => {
    if (value === undefined) return;
    if (value) params.set(key, String(value));
    else params.delete(key);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Dashboard URL that opens Create with agent, optionally locking a flow. */
export function agentCreatePath({ flowId = '', planId = 'new' } = {}) {
  return `/dashboard${withCreateAgentParams('', { planId: planId || 'new', flowId: flowId || null })}`;
}

/** True when a /create route should keep the wizard (resume / agent handoff), not redirect. */
export function hasCreateResumeIntent(searchParams, locationState) {
  const params = searchParams || new URLSearchParams();
  if (
    params.get('project')
    || params.get('projectId')
    || params.get('storyId')
    || params.get('jobId')
  ) {
    return true;
  }
  const state = locationState || {};
  return Boolean(state.storyFlow || state.agentAutoStart || state.prefill || state.quickIntake);
}

/** Video-editor path for an executed creation flow + project (mirrors backend editor_url). */
export function projectEditorPath(flowId, projectId) {
  const pid = String(projectId || '').trim();
  if (!pid) return '';
  const fid = String(flowId || '').trim();
  if (fid === 'dialogue') return `/stories/${pid}/video-editor`;
  if (fid === 'news_video') return `/news/${pid}/video-editor`;
  if (fid === 'reels_shorts') return `/reels-shorts/${pid}/video-editor`;
  if (['narration', 'script_based', 'song_based', 'storybuilder'].includes(fid)) {
    return `/narration/${pid}/video-editor`;
  }
  return '';
}

/** Find or create a slim executed plan for a Task History job (legacy rows). */
export async function planFromJob(jobId) {
  const { data } = await api.post(`/creation/plans/from-job/${jobId}`);
  return data?.plan || null;
}

export async function fetchJob(jobId) {
  const { data } = await api.get(`/jobs/${jobId}`);
  return data;
}

export async function fetchCreationPlan(planId) {
  const { data } = await api.get(`/creation/plans/${planId}`);
  return { plan: data.plan, transcript: Array.isArray(data.transcript) ? data.transcript : [] };
}

export async function listCreationPlans({ projectId, jobId, executed = false, limit = 50 } = {}) {
  const params = { limit };
  if (projectId) params.project_id = projectId;
  if (jobId) params.job_id = jobId;
  if (executed) params.executed = true;
  const { data } = await api.get('/creation/plans', { params });
  return Array.isArray(data?.plans) ? data.plans : [];
}

export async function saveCreationTranscript(planId, messages) {
  await api.put(`/creation/plans/${planId}/transcript`, {
    messages: messages.map((m) => ({ id: m.id, role: m.role, content: String(m.content || '') })),
  });
}

/**
 * Map executed plans to the tasks they started.
 * Always index by job id and by project id so resumed / sibling master jobs
 * (REEL&SHORTS, narration, dialogue, …) still get "Open in agent".
 */
export function indexPlansByTask(plans) {
  const byJob = new Map();
  const byProject = new Map();
  (plans || []).forEach((p) => {
    const exec = p?.execution;
    if (!exec || p.status !== 'executed') return;
    if (exec.job_id && !byJob.has(exec.job_id)) byJob.set(exec.job_id, p);
    if (exec.project_id && !byProject.has(exec.project_id)) byProject.set(exec.project_id, p);
  });
  return { byJob, byProject };
}

export function planForTask(index, job) {
  if (!index || !job) return null;
  const direct = index.byJob.get(job.id);
  if (direct) return direct;
  // Project-level match for master video/story tasks only (not segment/export children).
  if (!STORY_MASTER_JOB_TYPES.has(job.job_type)) return null;
  const projectIds = [
    job.project_id,
    job.narration_project_id,
    job.pitch_project_id,
    job.mascot_project_id,
    job.story_id,
  ];
  for (const pid of projectIds) {
    if (pid && index.byProject.has(pid)) return index.byProject.get(pid);
  }
  return null;
}

const FIELD_LABELS = {
  title: 'Title',
  brief: 'Brief',
  script: 'Script',
  language: 'Language',
  genre: 'Genres',
  tone: 'Tone',
  audience: 'Audience',
  'flow_params.story_format': 'Format',
  'flow_params.include_sfx': 'SFX cues',
  length: 'Length',
  scene_count: 'Scenes',
  duration_seconds: 'Duration',
  aspect_ratio: 'Format',
  'visual.style': 'Visual style',
  'visual.image_provider': 'Image model',
  'visual.video_provider': 'Video model',
  'voice.name': 'Voice',
  'voice.provider': 'Voice engine',
  'voice.style': 'Voice style',
  'voice.voice_id': 'Voice ID',
  'assets.audio': 'Audio',
  'assets.video': 'Video clips',
  'assets.document': 'PDF',
  'assets.image': 'Images',
  'options.subtitles': 'Subtitles',
  'options.music': 'Background music',
  'flow_params.recording_mode': 'Recording mode',
  'flow_params.characters': 'Characters',
  'flow_params.murf_locale': 'Murf locale',
  'flow_params.web_scraper_search_prompt': 'Web search prompt',
  'flow_params.anchor_name': 'Anchor name',
  'flow_params.screen_share': 'Screen share',
  'flow_params.speaker_camera': 'Speaker camera',
  'flow_params.camera': 'Camera',
  'flow_params.editing_mode': 'Editing mode',
  'flow_params.cleanup_level': 'Cleanup level',
  'flow_params.use_avatar': 'Show avatar',
  'flow_params.insert_types': 'Insert types',
  'flow_params.avatar_name': 'Avatar name',
  'flow_params.heygen_voice_id': 'HeyGen voice',
  flow_id: 'Video type',
};

export function fieldLabel(path) {
  return FIELD_LABELS[path] || path;
}

export function readField(input, path) {
  if (!input) return undefined;
  if (path.startsWith('assets.')) {
    const kind = path.split('.')[1];
    return (input.assets || []).filter((a) => a.kind === kind);
  }
  if (path === 'genre' && input.genres?.length) return input.genres;
  return path.split('.').reduce((cur, key) => (cur == null ? undefined : cur[key]), input);
}

export function isMultiChoice(question) {
  return Boolean(question && question.type === 'choice' && question.multiple);
}

export function canAnswerOther(question) {
  return Boolean(question && question.type === 'choice' && question.allow_custom && question.field !== 'flow_id');
}

export function initialChoicePicks(question) {
  if (!isMultiChoice(question)) return [];
  return (question.options || []).filter((o) => o.isDefault && !o.disabled).map((o) => o.value);
}

export function optionsHaveImages(question) {
  return Boolean(question?.options?.some((o) => o.image));
}

/**
 * Options to show inline under a question; the rest open in the "More" popup.
 * Suggested, currently picked and struck-out (unavailable) options always stay inline. Returns
 * everything when at most one option would be hidden.
 */
export function splitInlineOptions(options, { limit, picks = [] }) {
  const all = options || [];
  const inline = all.filter((o, i) => i < limit || o.isDefault || o.disabled || picks.includes(o.value));
  const hidden = all.length - inline.length;
  return hidden <= 1 ? { inline: all, hidden: 0 } : { inline, hidden };
}

export function splitCustomAnswer(text) {
  return String(text || '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Answer payload for a multi-select question: picked option values plus typed "Other" entries. */
export function buildMultiAnswer(question, picks, otherText = '') {
  const options = question?.options || [];
  const custom = splitCustomAnswer(otherText);
  const value = [...picks, ...custom.filter((c) => !picks.includes(c))];
  const labels = [
    ...picks.map((v) => options.find((o) => o.value === v)?.label || String(v)),
    ...custom.filter((c) => !picks.includes(c)),
  ];
  return { value, label: labels.join(', ') };
}

export function formatDuration(seconds) {
  const total = Math.round(Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  if (!minutes) return `${secs}s`;
  return secs ? `${minutes} min ${secs}s` : `${minutes} min`;
}

export function formatFieldValue(path, value) {
  if (value == null || value === '' || (Array.isArray(value) && !value.length)) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (path.startsWith('assets.')) return value.map((a) => a.filename || 'file').join(', ');
  if (path === 'flow_params.characters') return value.map((c) => c.name).join(', ');
  if (path === 'duration_seconds') return formatDuration(value);
  if (path === 'voice.provider') return VOICE_PROVIDER_LABELS[value] || (value === 'auto' ? 'Auto-pick' : String(value));
  if (path === 'flow_params.story_format') {
    return {
      dialogue: 'Dialogue',
      narrator_and_dialogue: 'Dialogue with narration',
      narrator: 'Narration',
    }[value] || String(value).replace(/_/g, ' ');
  }
  if (path === 'script') {
    const text = String(value).replace(/\s+/g, ' ').trim();
    return text.length > 72 ? `${text.slice(0, 72)}…` : text;
  }
  if (Array.isArray(value)) return value.map((v) => String(v).replace(/_/g, ' ')).join(', ');
  return String(value).replace(/_/g, ' ');
}
